import { useMutation } from '@apollo/client';
import classNames from 'classnames';
import { useFormik } from 'formik';
import { get, isArray, isEmpty } from 'lodash';
import { ChangeEvent, FC, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { useRecoilValue, useRecoilValueLoadable } from 'recoil';
import Button from '~/components/Button';
import Checkbox from '~/components/Checkbox';
import Input from '~/components/Input';
import Select from '~/components/Select';
import Colors from '~/constants/colors';
import { CREATE_CUSTOMER_ADDRESS_SQL, GET_LIST_ADDRESS_GQL, UPDATE_CUSTOMER_ADDRESS_SQL } from '~/data/user';
import { CountriesAtom, Country } from '~/services/countries';
import { UserAddress } from '~/services/user';
import ModalContainer from '../ModalContainer';
import styles from './AddressModal.module.scss';

interface AddressModalProps {
    onClose: () => void;
    open: boolean;
    selectedAddress?: Partial<UserAddress>;
    isSaveAddress?: boolean;
    isCheckoutPage?: boolean;
}

const AddressModal: FC<AddressModalProps> = ({
    onClose,
    open,
    selectedAddress = {},
    isSaveAddress,
    isCheckoutPage
}) => {
    const initialValues = {
        id: selectedAddress?.id || '',
        firstname: selectedAddress?.firstname || '',
        lastname: selectedAddress?.lastname || '',
        phone_number: selectedAddress?.telephone || '',
        address: typeof selectedAddress?.street === 'string' ? selectedAddress?.street : selectedAddress?.street?.[0],
        address1: selectedAddress?.street1,
        city: selectedAddress?.city || '',

        country: selectedAddress?.country_code || '',
        zip_code: selectedAddress?.postcode || '',
        is_default: selectedAddress?.default_shipping || false,
        is_save_address: false,
        region: selectedAddress?.region?.region_id?.toString() || ''
    };
    const { countries, countriesHasRegion } = useRecoilValue(CountriesAtom.countriesSelector);

    const [createAddress] = useMutation(CREATE_CUSTOMER_ADDRESS_SQL, {
        onError: ({ message }) => {
            toast.error(message);
        },
        onCompleted: () => {
            toast.success('Address has been added successfully');
            formik.resetForm({ values: initialValues });
            onClose();
        },
        // update: (cache, { data }) => {
        //   const existingAddress = cache.readQuery({
        //     query: GET_LIST_ADDRESS_GQL,
        //   }) as UseCustomerAddressResponse

        //   const newItems = data?.customer?.addresses || [];
        //   const oldItems = existingAddress?.customer?.addresses || [];

        //   cache.writeQuery({
        //     query: GET_LIST_ADDRESS_GQL,
        //     data: {
        //       customer: {
        //         addresses: [...oldItems, ...newItems],
        //       },
        //     },
        //   })
        // },
        refetchQueries: () => [{ query: GET_LIST_ADDRESS_GQL }]
    });

    const [updateAddress] = useMutation(UPDATE_CUSTOMER_ADDRESS_SQL, {
        onError: ({ message }) => {
            toast.error(message);
        },
        onCompleted: () => {
            toast.success('Address has been updated successfully');
            formik.resetForm({ values: initialValues });
            onClose();
        },
        // update: (cache, { data: { updateCustomerAddress } }) => {
        //   const existingAddress = cache.readQuery({
        //     query: GET_LIST_ADDRESS_GQL,
        //   }) as UseCustomerAddressResponse
        //   const newItems = existingAddress?.customer?.addresses?.map((addr) => {
        //     if (addr.id === updateCustomerAddress?.id) {
        //       return updateCustomerAddress
        //     }
        //     return addr
        //   })

        //   cache.writeQuery({
        //     query: GET_LIST_ADDRESS_GQL,
        //     data: {
        //       customer: {
        //         addresses: newItems
        //       },
        //     },
        //   })
        // },
        refetchQueries: () => [{ query: GET_LIST_ADDRESS_GQL }]
    });

    const formik = useFormik({
        enableReinitialize: true,
        initialValues,
        validate: (values) => {
            const errors: Record<string, string> = {};

            // if (!values.phone_number?.trim()) {
            //   errors.phone_number = "Please enter your phone number";
            // }

            if (!values.firstname?.trim()) {
                errors.firstname = 'Please enter your first name';
            }

            if (!values.lastname?.trim()) {
                errors.lastname = 'Please enter your last name';
            }

            if (!values.address?.trim()) {
                errors.address = 'Please enter your street address';
            }

            if (!values.city?.trim()) {
                errors.city = 'Please enter your town / city';
            }

            if (!values.zip_code?.trim()) {
                errors.zip_code = 'Please enter your ZIP code';
            }

            if (!values.country?.trim()) {
                errors.country = 'Please select your country';
            } else if (countriesHasRegion.includes(values.country) && !values?.region?.trim()) {
                errors.region = 'Please select your region';
            }

            return errors;
        },
        onSubmit: async (values) => {
            if (values?.id) {
                return await updateAddress({
                    variables: {
                        id: values.id,
                        countryCode: values.country,
                        firstname: values.firstname,
                        lastname: values.lastname,
                        company: '',
                        street: [values.address, values.address1]?.filter((v) => !!v) || [],
                        city: values.city,
                        regionId: values.region || undefined,
                        postcode: values.zip_code,
                        phonenumber: values.phone_number ?? undefined,
                        default_shipping: values.is_default
                    }
                });
            } else {
                return await createAddress({
                    variables: {
                        countryCode: values.country,
                        firstname: values.firstname,
                        lastname: values.lastname,
                        company: '',
                        street: [values.address, values.address1]?.filter((v) => !!v) || [],
                        city: values.city,
                        regionId: values.region || undefined,
                        postcode: values.zip_code,
                        phonenumber: values.phone_number,
                        default_shipping: values.is_default
                    }
                });
            }
        }
    });

    const avalidRegions: any[] = useMemo(() => {
        const result = countries?.find((c: Country) => c.value === formik.values.country)?.available_regions || [];
        return result?.map((r: any) => ({ label: r.name, value: r.id + '' })) || [];
    }, [countries, formik?.values.country]);

    return (
        <ModalContainer
            open={open}
            width={856}
            onClose={onClose}
            title={Object.keys(selectedAddress).length > 0 ? 'Edit Address' : 'Add new address'}
            titleIcon="location"
            className={styles.address_modal}
            type="bottom-sheet"
            titleColor={Colors.PRIMARY}
            afterClose={() => {
                formik.resetForm({
                    values: initialValues
                });
            }}
        >
            <div className="flex flex-col gap-3 md:flex-row">
                <Input
                    label="First name"
                    required
                    placeholder="Enter your first name"
                    className="flex-1"
                    {...formik.getFieldProps('firstname')}
                    error={formik.touched.firstname && formik.errors.firstname}
                />
                <Input
                    label="Last name"
                    required
                    placeholder="Enter your last name"
                    className="flex-1"
                    {...formik.getFieldProps('lastname')}
                    error={formik.touched.lastname && formik.errors.lastname}
                />
            </div>
            <Input
                label="Phone number"
                // required
                placeholder="Enter your phone number"
                {...formik.getFieldProps('phone_number')}
                error={formik.touched.phone_number && formik.errors.phone_number}
            />
            <Input
                label="Street Address"
                required
                placeholder="Enter street address"
                {...formik.getFieldProps('address')}
                error={formik.touched.address && formik.errors.address}
            />
            <Input
                placeholder="Enter Apartment, suite, etc. (optional)"
                {...formik.getFieldProps('address1')}
                error={formik.touched.address1 && formik.errors.address1}
            />
            <div className="flex flex-col gap-3 md:flex-row">
                <Input
                    label="Town / City"
                    required
                    placeholder="Enter town / city"
                    className="flex-1"
                    {...formik.getFieldProps('city')}
                    error={formik.touched.city && formik.errors.city}
                />
                <Input
                    label="ZIP"
                    required
                    placeholder="Enter ZIP code"
                    className="flex-1"
                    {...formik.getFieldProps('zip_code')}
                    error={formik.touched.zip_code && formik.errors.zip_code}
                />
            </div>
            <Select
                label="Country"
                required
                options={countries}
                value={formik.values.country}
                onChange={(value) => {
                    formik.setFieldValue('country', value);
                    formik.setFieldValue('region', '');
                }}
                placeHolder="Select country"
                error={formik.touched.country && formik.errors.country}
            />

            {!isEmpty(avalidRegions) && (
                <Select
                    required
                    options={avalidRegions}
                    label="Region"
                    value={get(formik, `values.region`) || ''}
                    onChange={(e) => {
                        formik.setFieldValue(`region`, e);
                    }}
                    error={get(formik, `touched.region`) && get(formik, `errors.region`)}
                />
            )}

            {!isCheckoutPage && (
                <Checkbox
                    {...formik.getFieldProps('is_default')}
                    checked={get(formik, `values.is_default`) || false}
                    onChange={(e) => {
                        formik.setFieldValue(`is_default`, e.target.checked);
                    }}
                    label="Set as default delivery address"
                    labelType="body-3"
                />
            )}
            <div className="py-4 sticky bottom-0 w-full bg-white">
                <Button
                    className={classNames(styles.button, 'w-full md:w-auto')}
                    onClick={formik.submitForm}
                    disabled={formik.isSubmitting || !formik.dirty}
                    loading={formik.isSubmitting}
                >
                    {Object.keys(selectedAddress).length > 0 ? 'Edit' : 'Add'} address
                </Button>
            </div>
        </ModalContainer>
    );
};

export default AddressModal;
